import { useEffect, useRef } from "react";
import {
  HubConnectionBuilder,
  HubConnection,
  LogLevel,
  HubConnectionState,
  IRetryPolicy,
  RetryContext,
} from "@microsoft/signalr";
import accountContext from "../../../account/accountContext";
import { useAppDispatch } from "../../hooks/sharedHooks";
import { addNotification, setUnreadCount, fetchUnreadCount } from "../notificationsSlice";
import { AppNotification } from "../models/notificationModels";
import { setSharedConnection } from "../signalrConnection";

/**
 * Exponential backoff retry policy that retries indefinitely.
 * Delays: 0, 2s, 4s, 8s, 16s, 30s, 30s, 30s, ... (capped at 30s)
 */
class ExponentialRetryPolicy implements IRetryPolicy {
  private static readonly MAX_DELAY_MS = 30000;

  nextRetryDelayInMilliseconds(retryContext: RetryContext): number | null {
    if (retryContext.previousRetryCount === 0) {
      return 0; // First retry is immediate
    }

    const delay = Math.min(
      1000 * Math.pow(2, retryContext.previousRetryCount),
      ExponentialRetryPolicy.MAX_DELAY_MS
    );
    return delay;
  }
}

const useNotificationHub = () => {
  const dispatch = useAppDispatch();
  const connectionRef = useRef<HubConnection | null>(null);

  useEffect(() => {
    const token = accountContext.accessToken;
    if (!token) {
      return;
    }

    const baseUrl = (window as any).app.config.notificationsApiBaseUrl;
    if (!baseUrl) {
      return;
    }

    const connection = new HubConnectionBuilder()
      .withUrl(`${baseUrl}/hubs/notifications`, {
        accessTokenFactory: () => accountContext.accessToken || "",
      })
      .withAutomaticReconnect(new ExponentialRetryPolicy())
      .configureLogging(LogLevel.Warning)
      .build();

    connection.on("ReceiveNotification", (notification: AppNotification) => {
      dispatch(addNotification(notification));
    });

    connection.on("UnreadCountUpdated", (count: number) => {
      dispatch(setUnreadCount(count));
    });

    // On reconnect, re-fetch unread count to sync badge
    connection.onreconnected(() => {
      dispatch(fetchUnreadCount());
    });

    connection
      .start()
      .catch((err) => console.error("SignalR connection error:", err));

    connectionRef.current = connection;
    setSharedConnection(connection);

    return () => {
      setSharedConnection(null);
      if (
        connectionRef.current &&
        connectionRef.current.state !== HubConnectionState.Disconnected
      ) {
        connectionRef.current.stop();
      }
    };
  }, [dispatch]);

  return connectionRef;
};

export default useNotificationHub;
